import { JamPadEvent } from '../DataModels.ts';
import { useState, useEffect, useRef, createContext } from 'react';
import { EventMapContext } from '../App/App.tsx';

import { UUID } from 'crypto';

import EventPreview from '../Events/EventPreview.tsx';
import EventItem from '../Events/EventItem.tsx';

import ArrowBack from '@mui/icons-material/ArrowBack';
import { supabase } from '../supabaseClient.js';
import { Box, Button, CircularProgress } from '@mui/material';
import { CancelRounded, Close, Place, Tune } from '@mui/icons-material';

export enum FeedFilter {
    today = "TODAY",
    tomorrow = "TOMORROW",
    week = "THIS WEEK",
    nextWeek = "NEXT WEEK",
    month = "THIS MONTH",
    nextMonth = "NEXT MONTH"
}

export type FeedCoord = {
    lat: number, lng: number,
    title: string
}

const locations = Array<FeedCoord>(
    { lat: 39.9625, lng: -83.0032, title: "Columbus" }, 
    { lat: 38.6274, lng: -90.1982, title: "St. Louis" }
)

export default function FeedPage() {
    const eventsPerLoad = 30;

    const initialized = useRef(false);
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState<Map<UUID, JamPadEvent>>(new Map<UUID, JamPadEvent>());
    const [moreEvents, setMoreEvents] = useState(true);
    const [selectedID, setID] = useState<UUID | undefined>(undefined);

    const [timeRange, setTimeRange] = useState<FeedFilter | null>(null);
    const [location, setLocation] = useState<FeedCoord | null>(null);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        if(!initialized.current) {
            setID(undefined);
            loadEventFeed();
            initialized.current = true
        }
    }, []);

    useEffect(() => {
        events.clear();
        setEvents(new Map());
        loadEventFeed();
    }, [timeRange, location])

    function loadEventFeed() {
        setLoading(true);
        navigator.geolocation.getCurrentPosition(async (res) => {
            var params: any = {
                lng_in: res.coords.longitude, lat_in: res.coords.latitude,
                offset_count: events.size, limit_count: eventsPerLoad
            };
            if(location !== null){
                params.lng_in = location.lng;
                params.lat_in = location.lat;
            }
            if(timeRange !== null) {
                params.filter = timeRange;
                params.time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            }
            console.log(params);
            const { data: newEvents, error} = await supabase
                .rpc("load_main_feed", params).select().overrideTypes<JamPadEvent[]>();
            if (error) {
                console.error("Failed to load feed: " + error.message)
            } else {
                const eventMap = new Map(events);
                newEvents.forEach((e) => {
                    e.date = new Date(e.date);
                    eventMap.set(e.id, e);
                })
                setMoreEvents(newEvents.length === eventsPerLoad);
                setEvents(eventMap);
            }
            setLoading(false);
        }, async (locErr) => {
            console.log("Failed to get location: " + locErr.message);
            // fallback to columbus -> 39.9625, 83.0032
            const { data: cbusEvents, error: noLocErr } = await supabase
                .rpc("load_main_feed", {
                    lat_in: location?.lat ?? locations[0].lat, lng_in: location?.lng ?? locations[0].lng, 
                    offset_count: events.size, limit_count: 30
                }).select().overrideTypes<JamPadEvent[]>();
            if (noLocErr) {
                console.error("Failed to load feed: " + noLocErr.message)
            } else {
                const eventMap = new Map(events);
                cbusEvents.forEach((e) => {
                    e.date = new Date(e.date);
                    eventMap.set(e.id, e);
                })
                setEvents(eventMap);
            }
            setLoading(false);
        })
    };

    return (<EventMapContext.Provider value={{ events, setEvents: (m) => {setEvents(m)} }}>
        <div style={{ 
            background: 'darkgray', display: 'flex', flexDirection: 'column',
            flex: '1', overflow: 'hidden', position: 'relative'
        }}>
            <div style={{ display: showFilters ? 'flex' : 'none', 
                background: 'lightgray', flexDirection: 'column', gap: '12px'
            }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', 
                    overflow: 'auto', padding: '12px 18px 0', flex: '1'
                }}>
                    { timeRange !== null && 
                        <CancelRounded sx={{ cursor: 'pointer', backgroundColor: 'white', borderRadius: '100px' }}
                            onClick={() => setTimeRange(null)} color='primary' />
                    }
                    { Object.values(FeedFilter).map((time) => {
                        return <Button key={time}
                            sx={{ flex: '1 0 140px', backgroundColor: timeRange === time ? 'darkblue' : 'white' }}
                            variant={timeRange === time ? 'contained' : 'outlined'}
                            onClick={() => {
                                setTimeRange(timeRange === time ? null : time)
                            }}>{time}</Button>;
                    })}
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'center', justifyContent: 'space-between', 
                    overflow: 'auto hidden', padding: '2px 18px 12px', flex: '1'
                }}>
                    <Button variant='contained' color='error' sx={{ 
                        borderRadius: '40px', padding: '6px 40px', gap: '6px', 
                        textWrap: 'nowrap', minWidth: '120px',
                    }} onClick={() => setShowFilters(false)}>Close<Close fontSize='small'/></Button>
                    <Button sx={{ 
                        textWrap: 'nowrap',
                        backgroundColor: location === null ? 'darkblue' : 'lightblue',
                        borderRadius: '20px', padding: '6px 40px', minWidth: '160px',
                        gap: '6px', alignItems: 'center'
                    }} variant={!location ? 'contained' : 'outlined'}
                    onClick={() => setLocation(null)}><Place fontSize='small'/>My Location</Button>

                    { locations.map((loc) => {
                        return <Button key={loc.title}
                            sx={{ textWrap: 'nowrap',
                                backgroundColor: loc === location ? 'darkblue' : 'lightblue',
                                borderRadius: '20px', padding: '6px 40px', minWidth: '140px',
                            }}
                            variant={loc === location ? 'contained' : 'outlined'}
                            onClick={() => {
                                setLocation(loc === location ? null : loc)
                            }}>{loc.title}</Button>;
                    })

                    }
                </div>
            </div>
            
            <div style={{ 
                background: '#272636', position: 'relative',
                flex: '1', overflow: 'hidden auto', margin: '0 auto',
                width: '100vw',
            }}>
                { events.size === 0 && !loading &&
                    <div style={{ margin: '0 auto', width: '100vw', textAlign: 'center', color: 'white', padding: '20px' }}>
                        <b>[ No events found ]</b>
                    </div> 
                }
                <Box sx={{ 
                    display: 'flex', gap: '10px', 
                    flexDirection: 'row', 
                    flexWrap: 'wrap',
                    padding: { xs: '10px 8px', md: '10px 0px' }, 
                    alignItems: 'center', justifyContent: 'center',
                }}>
                    { Array.from(events.values()).map((e) => {
                        return <Box key={e.id} sx={{ 
                            flex: { xs: '1 0 100%', md: '1 0 44%' }, 
                            maxWidth: { xs: '100%', md: '48%' }
                        }}>
                            <EventPreview key={e.id} event={e} 
                                seeFullEvent={() => { setID(e.id) }} 
                            />
                        </Box>
                    })}

                    { moreEvents && !loading &&
                        <Button variant='contained' sx={{
                            borderRadius: '18px', padding: '16px 32px', margin: '10px 10px 20px',
                            fontWeight: 'bold'
                        }} onClick={() => loadEventFeed() }>
                            Load More
                        </Button>
                    }
                </Box>
                { loading && 
                    <div style={{ margin: '0 auto', width: '100vw', textAlign: 'center', color: 'white', padding: '20px' }}>
                        <CircularProgress color="primary"></CircularProgress>
                    </div> 
                }
            </div>

            { !showFilters && 
                <div style={{ position: 'absolute' ,
                    top: '8px', left: '10px', zIndex: '2' 
                }} onClick={() => setShowFilters(!showFilters) }>
                    <Button sx={{ 
                        borderRadius: '100px', padding: '12px 0',
                        border: '2px solid white'
                    }} variant='contained'><Tune fontSize='small'/></Button>
                </div>
            }

            <div style={{
                background: 'darkgray', position: 'absolute', zIndex: '3',
                top: '0', left: '0', width: '100vw', height: '100%', overflow: 'hidden',
                display: selectedID !== undefined ? 'flex' : 'none'
            }}>
                <div style={{ padding: '14px', zIndex: '1', overflow: 'auto' }}>
                    <EventItem col={false} event={selectedID ? events.get(selectedID) : undefined} />
                </div>
                <div style={{ zIndex: '2', position: 'absolute', top: '4px', left: '4px'}}>
                    <Button onClick={() => { setID(undefined) }} variant='contained' sx={{ 
                        borderRadius: '32px', 
                        padding: "12px 0" 
                    }}>
                        <ArrowBack fontSize='medium'/></Button>
                </div>
            </div>
        </div>
        </EventMapContext.Provider>);
}